import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Dropdown,
  Input,
  DropdownItem,
} from 'design-system-web';
import { SourceType } from 'graphql-query-contracts';
import { Item } from 'components/form/Dropdown/Dropdown';
import { SourceTypeLabel } from '../../type-maps/source-type-label.map';
import { EditableExternalReferenceItem } from './types';

type ReferenceItemErrorField =
  keyof EditableExternalReferenceItem['errorMessage'];

function isDropdownItem(value: unknown): value is Item<string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    typeof value.value === 'string'
  );
}

const externalIdSourceOptions: DropdownItem<SourceType>[] = [
  {
    label: SourceTypeLabel[SourceType.SourceTypeActivate],
    value: SourceType.SourceTypeActivate,
    isDisabled: true,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeCbre],
    value: SourceType.SourceTypeCbre,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeMri],
    value: SourceType.SourceTypeMri,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeSfdc],
    value: SourceType.SourceTypeSfdc,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeVcm],
    value: SourceType.SourceTypeVcm,
  },
  {
    label: SourceTypeLabel[SourceType.SourceTypeYardi],
    value: SourceType.SourceTypeYardi,
  },
];

type Props = {
  reference: EditableExternalReferenceItem;
  editReference: (reference: EditableExternalReferenceItem) => void;
  removeReference: (referenceId: string) => void;
  disabled?: boolean;
};

export const EditExternalReferenceItem = ({
  reference,
  editReference,
  removeReference,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();

  const isDisabled = disabled || !reference.isRemovable;

  const handleOnChange = (
    fieldName: string,
    fieldValue: string | Item<string> | null
  ) => {
    let value = fieldValue;

    if (isDropdownItem(fieldValue)) {
      value = fieldValue.value;
    }

    const updatedReference = {
      ...reference,
      source: {
        ...reference.source,
        [fieldName]: value,
      },
    };

    editReference(updatedReference);
  };

  const handleRemove = () => {
    removeReference(reference.id);
  };

  const getErrorMessages = (fieldName: ReferenceItemErrorField) => {
    const errorMessage = reference.errorMessage[fieldName];

    if (errorMessage) {
      return [errorMessage];
    }

    return [];
  };

  const { sourceType, sourceEntityId } = reference.source;

  return (
    <Flex gap={4}>
      <Flex className="w-full">
        <Dropdown
          label={t('web.components.editExternalReferenceItem.sourceType.label')}
          fixedLabel
          testId="sourceTypeDropdown"
          placeholder={t(
            'web.components.editExternalReferenceItem.sourceType.placeholder'
          )}
          noOptionsMessage={t(
            'web.components.editExternalReferenceItem.sourceType.noOptions'
          )}
          items={externalIdSourceOptions}
          onChange={value => handleOnChange('sourceType', value)}
          value={sourceType}
          doTranslation={false}
          invalid={Boolean(getErrorMessages('sourceType').length)}
          errors={getErrorMessages('sourceType')}
          disabled={disabled}
          isClearable
        />
      </Flex>
      <Flex className="w-full">
        <Input
          fieldName="sourceEntityId"
          label={t(
            'web.components.editExternalReferenceItem.sourceEntityId.label'
          )}
          fixedLabel
          testId="sourceEntityIdInput"
          value={sourceEntityId}
          error={getErrorMessages('sourceEntityId')}
          onChange={value => handleOnChange('sourceEntityId', value)}
          placeholder={t(
            'web.components.editExternalReferenceItem.sourceEntityId.placeholder'
          )}
          disabled={disabled}
        />
      </Flex>
      <Flex className="items-start">
        <Button
          disabled={isDisabled}
          className="mt-4.5"
          startIcon={<Icon name="TrashAlt" />}
          variant="text"
          testId="remove-reference"
          onClick={handleRemove}
        />
      </Flex>
    </Flex>
  );
};
